import { API_BASE_URL } from './apiService';

class TimezoneService {
  constructor() {
    this.currentTimezone = 'Africa/Johannesburg'; // Default to South Africa timezone
    this.isLoaded = false;
    this.loadTimezone();
  }

  // Map Windows timezone IDs to IANA timezone IDs
  mapWindowsToIanaTimezone(windowsTimezoneId) {
    const timezoneMap = {
      'South Africa Standard Time': 'Africa/Johannesburg',
      'UTC': 'UTC',
      'GMT Standard Time': 'Europe/London',
      'Central European Standard Time': 'Europe/Paris',
      'Eastern Standard Time': 'America/New_York',
      'Pacific Standard Time': 'America/Los_Angeles',
      'China Standard Time': 'Asia/Shanghai',
      'Tokyo Standard Time': 'Asia/Tokyo',
      'India Standard Time': 'Asia/Kolkata',
      'AUS Eastern Standard Time': 'Australia/Sydney'
    };
    
    return timezoneMap[windowsTimezoneId] || windowsTimezoneId;
  }

  // Test if a timezone is supported by the browser
  isTimezoneSupported(timezoneId) {
    try {
      const testDate = new Date();
      testDate.toLocaleString('en-US', { timeZone: timezoneId });
      return true;
    } catch (error) {
      return false;
    }
  }

  async loadTimezone() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/systemsettings/timezone`);
      if (response.ok) {
        const settings = await response.json();
        // Map Windows timezone ID to IANA timezone ID
        const mappedTimezone = this.mapWindowsToIanaTimezone(settings.timezoneId);
        
        // Test if the mapped timezone is supported by the browser
        if (this.isTimezoneSupported(mappedTimezone)) {
          this.currentTimezone = mappedTimezone;
          console.log('Timezone loaded and supported:', settings.timezoneId, '->', this.currentTimezone);
        } else {
          console.warn('Mapped timezone not supported by browser, trying alternatives:', mappedTimezone);
          // Try alternative South Africa timezones
          const alternatives = ['Africa/Johannesburg', 'Africa/Harare', 'Africa/Lagos'];
          for (const alt of alternatives) {
            if (this.isTimezoneSupported(alt)) {
              this.currentTimezone = alt;
              console.log('Using alternative timezone:', alt);
              break;
            }
          }
          if (!this.currentTimezone || this.currentTimezone === 'UTC') {
            this.currentTimezone = 'UTC';
            console.warn('No supported South Africa timezone found, using UTC');
          }
        }
        this.isLoaded = true;
      } else {
        // If no timezone is set, default to South Africa timezone
        this.currentTimezone = 'Africa/Johannesburg';
        this.isLoaded = true;
        console.log('Using default timezone:', this.currentTimezone);
      }
    } catch (error) {
      console.warn('Failed to load timezone settings, using South Africa timezone:', error);
      // Default to South Africa timezone instead of UTC
      this.currentTimezone = 'Africa/Johannesburg';
      this.isLoaded = true;
    }
  }

  async ensureTimezoneLoaded() {
    if (!this.isLoaded) {
      await this.loadTimezone();
    }
  }

  async formatDateTime(dateTime, options = {}) {
    await this.ensureTimezoneLoaded();
    
    if (!dateTime) return '';
    
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';

    const defaultOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: this.currentTimezone
    };

    const finalOptions = { ...defaultOptions, ...options };
    
    try {
      const result = date.toLocaleString('en-US', finalOptions);
      console.log(`Timezone conversion: ${dateTime} -> ${result} (using ${this.currentTimezone})`);
      return result;
    } catch (error) {
      console.warn(`Timezone conversion failed for ${this.currentTimezone}, trying fallbacks:`, error);
      // Try fallback timezones for South Africa
      const fallbackTimezones = [
        'Africa/Johannesburg',
        'Africa/Harare',
        'Africa/Lagos',
        'UTC'
      ];
      
      for (const fallbackTz of fallbackTimezones) {
        try {
          finalOptions.timeZone = fallbackTz;
          const fallbackResult = date.toLocaleString('en-US', finalOptions);
          return fallbackResult;
        } catch (fallbackError) {
          continue;
        }
      }
      
      // If all fallbacks fail, try manual conversion
      return this.convertToLocalTimeManual(dateTime);
    }
  }

  async formatDate(dateTime) {
    return this.formatDateTime(dateTime, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  async formatTime(dateTime) {
    return this.formatDateTime(dateTime, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  async formatRelativeTime(dateTime) {
    if (!dateTime) return '';
    
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return this.formatDate(dateTime);
    }
  }

  getCurrentTimeInTimezone(timezoneId) {
    try {
      const now = new Date();
      const mappedTimezone = this.mapWindowsToIanaTimezone(timezoneId);
      const options = {
        timeZone: mappedTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      return now.toLocaleString('en-US', options);
    } catch (error) {
      // Try fallback timezones for South Africa
      const fallbackTimezones = [
        'Africa/Johannesburg',
        'Africa/Harare',
        'Africa/Lagos',
        'UTC'
      ];
      
      for (const fallbackTz of fallbackTimezones) {
        try {
          const now = new Date();
          const options = {
            timeZone: fallbackTz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          };
          return now.toLocaleString('en-US', options);
        } catch {
          continue;
        }
      }
      
      return 'Invalid timezone';
    }
  }

  convertToLocalTime(utcTime) {
    if (!utcTime) return null;
    
    try {
      const date = new Date(utcTime);
      if (isNaN(date.getTime())) return null;
      
      return this.formatDateTime(date);
    } catch (error) {
      console.warn('Failed to convert UTC time to local:', error);
      return utcTime;
    }
  }

  getTimezoneOffset() {
    try {
      const now = new Date();
      const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const timezoneDate = new Date(utc.toLocaleString("en-US", { timeZone: this.currentTimezone }));
      const offset = (timezoneDate.getTime() - utc.getTime()) / (1000 * 60 * 60);
      return offset;
    } catch {
      return 0;
    }
  }

  // Manual timezone conversion as fallback
  convertToLocalTimeManual(utcTime) {
    if (!utcTime) return '';
    
    const date = new Date(utcTime);
    if (isNaN(date.getTime())) return '';

    // For South Africa, add 2 hours to UTC
    const southAfricaOffset = 2; // UTC+2
    const localTime = new Date(date.getTime() + (southAfricaOffset * 60 * 60 * 1000));
    
    return localTime.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }


}

export const timezoneService = new TimezoneService(); 